import React, { useCallback, useRef } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../../../common/types';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useIsNativeEmbed } from '../../../music24/navigation/hooks/useIsNativeEmbed';
import { catalogSongMessages } from './catalogSongMessages';
import { formatSongLength, MASTER_ACCEPT } from './masterFile';
import { useMasterUpload } from './useMasterUpload';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song files; each part uses its own slice of it */
import styles from './SongFiles.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogSongCellProps {
  accountId: number;
  item: CatalogItem;
  /** Opens the song page at Song files (design song-page a1), or the native song page in the apps. */
  onOpen: (item: CatalogItem) => void;
}

/**
 * The Song column of the catalog (design f1): what the row holds (master, lyrics, length, BPM) or
 * "+ Add the master". In a browser, "+ Add the master" opens the file picker and uploads at once;
 * the first upload of an account opens the sheet instead, because it needs the rights box (g1).
 * In the native apps every click opens the native sheet, which uploads in the background.
 */
const CatalogSongCellComponent: React.FC<CatalogSongCellProps> = ({ accountId, item, onOpen }) => {
  const intl = useIntl();
  const isNativeEmbed = useIsNativeEmbed();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { upload, cancel, canStop, progress, error, needsRights, finishingName } = useMasterUpload({
    accountId,
    itemUuid: item.uuid,
  });
  const { song } = item;

  const open = useCallback(() => onOpen(item), [item, onOpen]);

  const addMaster = useCallback(() => {
    if (isNativeEmbed || needsRights) {
      onOpen(item);
      return;
    }
    inputRef.current?.click();
  }, [isNativeEmbed, item, needsRights, onOpen]);

  const onFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (file !== undefined) {
        upload(file, false).catch(console.error);
      }
    },
    [upload]
  );

  if (progress !== null) {
    return (
      <span className={styles.cell} role="status">
        <span className={styles.cellSub}>
          {intl.formatMessage(catalogSongMessages.uploading, { percent: progress })}
        </span>
        <span className={styles.cellBar}>
          <i style={{ width: `${progress}%` }} />
        </span>
        {canStop && (
          <button type="button" className={styles.linkButton} onClick={cancel}>
            {intl.formatMessage(catalogSongMessages.stop)}
          </button>
        )}
      </span>
    );
  }

  if (finishingName !== undefined) {
    return (
      <span className={styles.cell} role="status">
        <span className={styles.cellSub}>{intl.formatMessage(catalogSongMessages.finishing)}</span>
      </span>
    );
  }

  if (!song.hasMaster) {
    return (
      <span className="d-flex flex-column gap-1">
        <input ref={inputRef} type="file" accept={MASTER_ACCEPT} hidden onChange={onFile} />
        <button type="button" className={styles.addMaster} onClick={addMaster}>
          {intl.formatMessage(catalogSongMessages.cellAdd)}
        </button>
        {error !== null && <span className={styles.error}>{error}</span>}
      </span>
    );
  }

  const label = intl.formatMessage(catalogSongMessages.cellOpen, { title: item.title });

  if (song.masterStatus === 'processing') {
    return (
      <button type="button" className={styles.cell} aria-label={label} onClick={open}>
        <span className={styles.cellLine}>{intl.formatMessage(catalogSongMessages.cellMaster)}</span>
        <span className={styles.cellSub}>{intl.formatMessage(catalogSongMessages.cellProcessing)}</span>
      </button>
    );
  }

  const isFailed = song.masterStatus === 'failed';
  // Design f1: "3:12 · 118 BPM" with lyrics, "2:58 · NO LYRICS" without.
  const bpm = song.bpm === null ? null : intl.formatMessage(catalogSongMessages.bpm, { bpm: Math.round(song.bpm) });
  const facts = [
    song.durationSec === null ? null : formatSongLength(song.durationSec),
    song.hasLyrics ? bpm : intl.formatMessage(catalogSongMessages.cellNoLyrics),
  ];
  const sub = isFailed
    ? intl.formatMessage(catalogSongMessages.cellFailed)
    : facts.filter((part): part is string => part !== null).join(' · ');

  return (
    <button type="button" className={styles.cell} aria-label={label} onClick={open}>
      <span className={styles.cellLine}>
        <span className={clsx(styles.okDot, isFailed && styles.failDot)}>
          <FontAwesomeIcon icon={isFailed ? faXmark : faCheck} />
        </span>
        {intl.formatMessage(song.hasLyrics ? catalogSongMessages.cellMasterLyrics : catalogSongMessages.cellMaster)}
      </span>
      {sub !== '' && <span className={styles.cellSub}>{sub}</span>}
    </button>
  );
};

export const CatalogSongCell = TypedMemo(CatalogSongCellComponent);
